import { z } from 'zod';

/** Consistent API error envelope (§28.2). */
export const ErrorCode = z.enum([
  'AUTH_REQUIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'FEATURE_DISABLED',
  'GIG_NOT_ACCEPTING_INTERACTIONS',
  'INTEGRATION_NOT_READY',
  'VALIDATION_FAILED',
  'VERSION_CONFLICT',
  'DUPLICATE_SUBMISSION',
  'RATE_LIMITED',
  'PAYMENT_PROVIDER_ERROR',
  'NOTIFICATION_PROVIDER_ERROR',
  'TEMPORARILY_UNAVAILABLE',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const FieldError = z.object({
  path: z.string(),
  message: z.string(),
});
export type FieldError = z.infer<typeof FieldError>;

export const ErrorEnvelope = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    fieldErrors: z.array(FieldError).default([]),
    retryAfterSeconds: z.number().int().positive().nullable().default(null),
  }),
  requestId: z.string(),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

/** HTTP status mapping for each error code. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  AUTH_REQUIRED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  FEATURE_DISABLED: 403,
  GIG_NOT_ACCEPTING_INTERACTIONS: 409,
  INTEGRATION_NOT_READY: 409,
  VALIDATION_FAILED: 400,
  VERSION_CONFLICT: 409,
  DUPLICATE_SUBMISSION: 409,
  RATE_LIMITED: 429,
  PAYMENT_PROVIDER_ERROR: 502,
  NOTIFICATION_PROVIDER_ERROR: 502,
  TEMPORARILY_UNAVAILABLE: 503,
};

export interface AppErrorOptions {
  message?: string;
  fieldErrors?: FieldError[];
  retryAfterSeconds?: number | null;
  cause?: unknown;
}

const DEFAULT_MESSAGES: Record<ErrorCode, string> = {
  AUTH_REQUIRED: 'Authentication is required.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  FEATURE_DISABLED: 'This feature is not available for this gig.',
  GIG_NOT_ACCEPTING_INTERACTIONS: 'This gig is not currently accepting interactions.',
  INTEGRATION_NOT_READY: 'A required integration is not ready.',
  VALIDATION_FAILED: 'The request was invalid.',
  VERSION_CONFLICT: 'The resource was modified by someone else. Refresh and try again.',
  DUPLICATE_SUBMISSION: 'This submission was already received.',
  RATE_LIMITED: 'Too many requests. Please try again shortly.',
  PAYMENT_PROVIDER_ERROR: 'The payment provider returned an error.',
  NOTIFICATION_PROVIDER_ERROR: 'The notification provider returned an error.',
  TEMPORARILY_UNAVAILABLE: 'The service is temporarily unavailable.',
};

/**
 * Domain/application error carrying a stable code. Handlers convert this into an
 * {@link ErrorEnvelope}. Never leak provider internals or detection details (§25.4, §33).
 */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly fieldErrors: FieldError[];
  readonly retryAfterSeconds: number | null;

  constructor(code: ErrorCode, options: AppErrorOptions = {}) {
    super(options.message ?? DEFAULT_MESSAGES[code], { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    this.fieldErrors = options.fieldErrors ?? [];
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }

  get httpStatus(): number {
    return ERROR_STATUS[this.code];
  }

  toEnvelope(requestId: string): ErrorEnvelope {
    return {
      error: {
        code: this.code,
        message: this.message,
        fieldErrors: this.fieldErrors,
        retryAfterSeconds: this.retryAfterSeconds,
      },
      requestId,
    };
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}
